import type { Vector2 } from '../core/constants';

/**
 * Surface-following crawl used by the J.Frog. Instead of walking only the top
 * of the terrain (and halting at walls), the crawler hugs the terrain CONTOUR
 * and follows it all the way around - up vertical faces, across overhangs and
 * along undersides/ceilings - so the frog traces the entire perimeter of a
 * landmass in a fixed orientation (clockwise or counter-clockwise).
 *
 * The terrain is treated as a bitmap (isSolid). Each step the crawler:
 *   1. moves a short distance along the surface tangent (perpendicular to the
 *      outward normal, oriented by the crawl direction);
 *   2. re-acquires the nearest surface point around the tentative position;
 *   3. re-estimates the outward normal there and floats just off the surface.
 * Re-acquiring the *nearest* surface each micro-step is what lets it pivot
 * around convex corners (the corner becomes the pivot point and the normal
 * rotates) and climb into concave corners (the wall becomes the nearest
 * surface and the normal turns away from it). The math is pure and
 * deterministic, so it behaves identically on the server and every client.
 */

export interface SurfaceState {
  /** Body-centre position, floated just off the surface. */
  x: number;
  y: number;
  /** Outward (away-from-terrain) unit normal at the contact point. */
  nx: number;
  ny: number;
}

/** Disc radius (px) sampled when estimating the local surface normal. */
const SAMPLE_RADIUS = 4;
/** How far (px) the body centre floats off the surface. */
const FLOAT_OFFSET = 3;
/** Max search distance (px) when re-acquiring the surface. */
const MAX_REACH = 10;
/** Integration micro-step (px); small steps keep it glued through corners. */
const MICRO_STEP = 1;

function isSolidAt(
  terrain: { isSolid(x: number, y: number): boolean },
  x: number,
  y: number,
): boolean {
  return terrain.isSolid(Math.round(x), Math.round(y));
}

/**
 * Estimate the outward surface normal at (x, y) from the local mask: sum the
 * vectors pointing away from each nearby solid sample. Falls back to the
 * previous normal when the sample is degenerate (e.g. fully solid/empty).
 */
export function estimateSurfaceNormal(
  terrain: { isSolid(x: number, y: number): boolean },
  x: number,
  y: number,
  fallback: Vector2,
): Vector2 {
  let sx = 0;
  let sy = 0;
  let count = 0;
  for (let dx = -SAMPLE_RADIUS; dx <= SAMPLE_RADIUS; dx++) {
    for (let dy = -SAMPLE_RADIUS; dy <= SAMPLE_RADIUS; dy++) {
      if (dx * dx + dy * dy > SAMPLE_RADIUS * SAMPLE_RADIUS) continue;
      if (isSolidAt(terrain, x + dx, y + dy)) {
        sx -= dx;
        sy -= dy;
        count++;
      }
    }
  }
  if (count === 0) return { x: fallback.x, y: fallback.y };
  const len = Math.hypot(sx, sy);
  if (len < 1e-6) return { x: fallback.x, y: fallback.y };
  return { x: sx / len, y: sy / len };
}

/**
 * Signed distance from (cx, cy) to the surface measured along the normal:
 * positive when the point is OUTSIDE the terrain (the surface lies `d` px in
 * the -normal direction), negative when embedded. null when no surface is
 * within reach along that line. Used to keep the body floating a fixed offset
 * off the surface WITHOUT discarding tangential progress (the bug that stalls
 * a nearest-pixel snap at convex corners).
 */
function surfaceOffsetAlongNormal(
  terrain: { isSolid(x: number, y: number): boolean },
  cx: number,
  cy: number,
  nx: number,
  ny: number,
): number | null {
  if (!isSolidAt(terrain, cx, cy)) {
    for (let k = 1; k <= MAX_REACH; k++) {
      if (isSolidAt(terrain, cx - nx * k, cy - ny * k)) return k;
    }
    return null;
  }
  // Embedded: march outward to the first empty cell; we are ~(k-1) px inside.
  for (let k = 1; k <= MAX_REACH; k++) {
    if (!isSolidAt(terrain, cx + nx * k, cy + ny * k)) return -(k - 1);
  }
  return null;
}

/**
 * Nearest solid pixel to (cx, cy) within MAX_REACH, or null if none. Scans in
 * a deterministic order so ties resolve identically everywhere.
 */
function nearestSurfacePoint(
  terrain: { isSolid(x: number, y: number): boolean },
  cx: number,
  cy: number,
  maxReach: number = MAX_REACH,
): Vector2 | null {
  let best: Vector2 | null = null;
  let bestD2 = Infinity;
  for (let dx = -maxReach; dx <= maxReach; dx++) {
    for (let dy = -maxReach; dy <= maxReach; dy++) {
      const d2 = dx * dx + dy * dy;
      if (d2 > maxReach * maxReach || d2 >= bestD2) continue;
      if (isSolidAt(terrain, cx + dx, cy + dy)) {
        bestD2 = d2;
        best = { x: cx + dx, y: cy + dy };
      }
    }
  }
  return best;
}

/**
 * Acquire an initial crawl state at/near (cx, cy): snap onto the nearest
 * surface and compute its outward normal. Returns null if there is no terrain
 * within reach (still airborne).
 */
export function acquireSurface(
  terrain: { isSolid(x: number, y: number): boolean },
  cx: number,
  cy: number,
  fallbackNormal: Vector2 = { x: 0, y: -1 },
): SurfaceState | null {
  const surf = nearestSurfacePoint(terrain, cx, cy);
  if (!surf) return null;
  const n = estimateSurfaceNormal(terrain, surf.x, surf.y, fallbackNormal);
  return {
    x: surf.x + n.x * FLOAT_OFFSET,
    y: surf.y + n.y * FLOAT_OFFSET,
    nx: n.x,
    ny: n.y,
  };
}

/**
 * Advance the crawl state along the surface by `distance` px in the given
 * direction ('right' = clockwise around the landmass, 'left' = counter-
 * clockwise). Returns the new state and whether contact was lost (walked off
 * an isolated tip), in which case the caller should let the frog fall.
 */
export function crawlAlongSurface(
  terrain: { isSolid(x: number, y: number): boolean },
  state: SurfaceState,
  direction: 'left' | 'right',
  distance: number,
): { state: SurfaceState; lostContact: boolean } {
  let { x, y, nx, ny } = state;
  let remaining = distance;

  while (remaining > 1e-6) {
    const ds = Math.min(MICRO_STEP, remaining);
    remaining -= ds;

    // Tangent perpendicular to the outward normal, oriented by direction.
    // right -> (-ny, nx) keeps terrain on a consistent side (clockwise).
    const tx = direction === 'right' ? -ny : ny;
    const ty = direction === 'right' ? nx : -nx;

    // Advance tangentially first, then correct ONLY along the normal so the
    // tangential progress survives - this is what lets the contact pivot
    // around convex corners and climb into concave ones.
    const cx = x + tx * ds;
    const cy = y + ty * ds;

    const surf = nearestSurfacePoint(terrain, cx, cy);
    if (!surf) {
      return { state: { x, y, nx, ny }, lostContact: true };
    }

    let n = estimateSurfaceNormal(terrain, surf.x, surf.y, { x: nx, y: ny });
    // Reject spurious ~180 flips (thin terrain ambiguity); keep prior normal.
    if (n.x * nx + n.y * ny < 0) n = { x: nx, y: ny };

    const d = surfaceOffsetAlongNormal(terrain, cx, cy, n.x, n.y);
    if (d === null) {
      // Off the normal line entirely (rare): snap to the nearest surface.
      x = surf.x + n.x * FLOAT_OFFSET;
      y = surf.y + n.y * FLOAT_OFFSET;
    } else {
      // Slide along the normal to restore the float offset, keeping (cx, cy)'s
      // tangential component.
      const corr = FLOAT_OFFSET - d;
      x = cx + n.x * corr;
      y = cy + n.y * corr;
    }
    nx = n.x;
    ny = n.y;
  }

  return { state: { x, y, nx, ny }, lostContact: false };
}
